import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  identityApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { githubWorkflowsApiRef } from '@veecode-platform/github-workflows-common';

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: githubWorkflowsApiRef,
    deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, identityApi, fetchApi }) => {
      const authFetch = async (input: RequestInfo, init: RequestInit = {}) => {
        const { token } = await identityApi.getCredentials();
        const headers = new Headers(init.headers || {});
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        // Ensure JSON content-type is preserved when provided
        if (init.body && !headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }
        return fetchApi.fetch(input, { ...init, headers });
      };

      const getBaseUrl = async () => discoveryApi.getBaseUrl('github-workflow-backend');

      return {
        async listWorkflows(hostname: string, githubRepoSlug: string, branch: string, filter?: string[]) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug, branch });
          if (filter && filter.length > 0) params.set('filter', filter.join(','));

          // Add pagination for exo-imaging repos to reduce payload
          if (githubRepoSlug.startsWith('exo-imaging/')) {
            params.set('per_page', '50');
          }

          const url = `${baseUrl}/workflows?${params.toString()}`;

          try {
            const res = await authFetch(url);
            if (!res.ok) {
              // On backend failure for exo-imaging repos, show a helpful placeholder row
              if (githubRepoSlug.startsWith('exo-imaging/')) {
                console.warn(`Workflows endpoint failed for ${githubRepoSlug}: ${res.status}`);
                return [
                  {
                    workflow: {
                      id: 0,
                      name: 'Workflows temporarily unavailable (open Actions in GitHub)',
                      state: 'disabled',
                      url: `https://github.com/${githubRepoSlug}/actions`,
                      path: '.github/workflows/info.yaml',
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    },
                    latestRun: { status: 'completed', conclusion: 'neutral' },
                    parameters: [],
                  },
                ];
              }
              throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
            }

            const data = await res.json();

            // Handle different response formats
            if (Array.isArray(data)) {
              return data; // Direct array response
            }
            if (data && Array.isArray(data.workflows)) {
              return data.workflows; // Object with workflows array
            }
            if (data && data.error) {
              // For exo-imaging repos, return placeholder instead of empty view
              if (githubRepoSlug.startsWith('exo-imaging/')) {
                console.warn(`Backend reported error for ${githubRepoSlug}: ${data.error?.message ?? data.error}`);
                return [
                  {
                    workflow: {
                      id: 0,
                      name: 'Error loading workflows; use CI/CD tab or open GitHub Actions',
                      state: 'disabled',
                      url: `https://github.com/${githubRepoSlug}/actions`,
                      path: '.github/workflows/info.yaml',
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    },
                    latestRun: { status: 'completed', conclusion: 'neutral' },
                    parameters: [],
                  },
                ];
              }
              return [];
            }

            console.warn(`Unexpected workflows response format for ${githubRepoSlug}`, data);
            return [];
          } catch (error) {
            console.error(`Error fetching workflows for ${githubRepoSlug}:`, error);
            // Gracefully degrade for exo-imaging repos with an informative row
            if (githubRepoSlug.startsWith('exo-imaging/')) {
              return [
                {
                  workflow: {
                    id: 0,
                    name: 'Unable to load workflows — open Actions in GitHub',
                    state: 'disabled',
                    url: `https://github.com/${githubRepoSlug}/actions`,
                    path: '.github/workflows/info.yaml',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  },
                  latestRun: { status: 'completed', conclusion: 'neutral' },
                  parameters: [],
                },
              ];
            }
            return [];
          }
        },
        async listBranchesFromRepo(hostname: string, githubRepoSlug: string) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug });
          const url = `${baseUrl}/branches?${params.toString()}`;
          const res = await authFetch(url);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
        async getBranchDefaultFromRepo(hostname: string, githubRepoSlug: string) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug });
          const url = `${baseUrl}/default-branch?${params.toString()}`;
          const res = await authFetch(url);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
        async startWorkflowRun(hostname: string, githubRepoSlug: string, workflowId: number, branch: string, inputs?: Record<string, unknown>) {
          const baseUrl = await getBaseUrl();
          const url = `${baseUrl}/start-workflow`;
          const body: any = { hostname, githubRepoSlug, workflowId, branch };
          if (inputs) body.inputs = inputs;
          const res = await authFetch(url, { method: 'POST', body: JSON.stringify(body) });
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          const data = await res.json();
          return data.status;
        },
        async stopWorkflowRun(hostname: string, githubRepoSlug: string, runId: number) {
          const baseUrl = await getBaseUrl();
          const url = `${baseUrl}/stop-workflow`;
          const body = { hostname, githubRepoSlug, runId };
          const res = await authFetch(url, { method: 'POST', body: JSON.stringify(body) });
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          const data = await res.json();
          return data.status;
        },
        async listJobsForWorkflowRun(hostname: string, githubRepoSlug: string, id: number, pageSize?: number, page?: number) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug, id: String(id) });
          if (pageSize) params.set('pageSize', String(pageSize));
          if (page) params.set('page', String(page));
          const url = `${baseUrl}/jobs?${params.toString()}`;
          const res = await authFetch(url);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
        async getWorkflowRunById(hostname: string, githubRepoSlug: string, runId: number) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug, runId: String(runId) });
          const url = `${baseUrl}/workflow-run?${params.toString()}`;
          const res = await authFetch(url);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
        async downloadJobLogsForWorkflowRun(hostname: string, githubRepoSlug: string, jobId: number) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug, jobId: String(jobId) });
          const url = `${baseUrl}/download-logs?${params.toString()}`;
          const res = await authFetch(url);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
        async getEnvironmentsList(hostname: string, githubRepoSlug: string) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug });
          const url = `${baseUrl}/environments?${params.toString()}`;
          const res = await authFetch(url);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
        async listRunHistory(hostname: string, githubRepoSlug: string, workflowNameFilter: string, limit = 20) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug, workflowNameFilter, limit: String(limit) });
          const url = `${baseUrl}/run-history?${params.toString()}`;
          const res = await authFetch(url);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
        async getServiceTags(hostname: string, githubRepoSlug: string) {
          const baseUrl = await getBaseUrl();
          const params = new URLSearchParams({ hostname, githubRepoSlug });
          const url = `${baseUrl}/service-tags?${params.toString()}`;
          const res = await authFetch(url);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
        async getDeployedVersions() {
          const baseUrl = await getBaseUrl();
          const res = await authFetch(`${baseUrl}/deployed-versions`);
          if (!res.ok) throw new Error(`Backend API error: ${res.status} ${res.statusText}`);
          return res.json();
        },
      };
    },
  }),
];
